import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Contadores } from '@auth/interfaces/dadoslogin.interface';
import { DadosLoginService } from '@auth/services/dados-login.service';
import { PortalService } from '@auth/services/portal.service';
import { SideMenu } from 'src/app/models';
import { ApiProjetoService } from 'src/app/portal-coordenador/services/api-projeto.service';
import { links } from './links';

@Injectable({ providedIn: 'root' })
export class SidebarLinksService implements OnDestroy {
    private readonly links = links;
    private readonly linksSubject = new BehaviorSubject<SideMenu[]>([]);
    readonly links$ = this.linksSubject.asObservable();
    private readonly destroy$ = new Subject<void>();

    constructor(
        private apiProjetoCoordenadorService: ApiProjetoService,
        private dadosLoginService: DadosLoginService,
        private portalService: PortalService
    ) {
        this.observarPortal();
        this.observarDadosLogin();
        this.observarProjetoCoordenador();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // OBSERVADORES: gatilhos para atualizar links.ts
    private observarPortal() {
        this.portalService.portalAtual$
            ?.pipe(takeUntil(this.destroy$))
            .subscribe(this.atualizarLinks);
    }

    private observarDadosLogin() {
        this.dadosLoginService.dadosLogin$
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: dados => {
                    const contadores = dados.contadores;
                    this.updateAllBadges(contadores);
                },
                error: err => {
                    console.error(err);
                },
            });
    }

    private observarProjetoCoordenador() {
        this.apiProjetoCoordenadorService.projeto$
            .pipe(takeUntil(this.destroy$))
            .subscribe(projeto => {
                if (!projeto) return;
                const contadores = this.dadosLoginService.getContadores();
                if (contadores) {
                    this.updateBadgesCoordenador(contadores);
                    this.atualizarLinks(
                        this.portalService.getPortalAtual()?.toLowerCase()
                    );
                }
            });
    }

    // Atualizar links
    private atualizarLinks = (portal: string | undefined) => {
        this.linksSubject.next(this.links[portal] ?? []);
    };

    private updateAllBadges(contadores: Contadores) {
        this.updateBadgesColaborador(contadores);
        this.updateBadgesAdministrador(contadores);
        this.updateBadgesCoordenador(contadores);
        this.atualizarLinks(this.portalService.getPortalAtual()?.toLowerCase());
    }

    // HELPERS: métodos auxiliares para atualizar os badges
    private criarBadge(valor: number): string {
        return `<span class="badge badge-${
            valor > 0 ? 'info' : 'secondary'
        }">${valor}</span>`;
    }

    private getStatusKey(id: string): string {
        return id.split('-').join('_');
    }

    private processarBadgeSimples(
        submenu: SideMenu,
        contadores: Contadores
    ): boolean {
        const statusKey = this.getStatusKey(submenu.id);
        if (statusKey in contadores) {
            const valor = contadores[statusKey] || 0;
            submenu.badge = this.criarBadge(valor);
            return valor > 0;
        }
        submenu.badge = undefined;
        return false;
    }

    // COLABORADOR
    private updateBadgesColaborador(contadores: Contadores) {
        this.links['colaborador'] = this.links['colaborador'].map(menu => {
            if (
                menu.id === 'funcional' ||
                menu.id === 'requisicoes' ||
                menu.id === 'org-funcionamento'
            ) {
                let requer_atencao = false;

                const submenus = menu.submenus.map(submenu => {
                    if (
                        submenu.id === 'tarefas_atender' &&
                        menu.id === 'org-funcionamento'
                    ) {
                        const resultado = this.processarTarefasColaborador(
                            submenu,
                            contadores
                        );
                        requer_atencao = resultado.requer_atencao;
                        return resultado.submenu;
                    }

                    if (this.processarBadgeSimples(submenu, contadores)) {
                        requer_atencao = true;
                    }
                    return submenu;
                });

                return {
                    ...menu,
                    warn: requer_atencao,
                    submenus,
                };
            }

            return menu;
        });
    }

    private processarTarefasColaborador(
        submenu: SideMenu,
        contadores: Contadores
    ): { submenu: SideMenu; requer_atencao: boolean } {
        const atenderSubmenu = submenu.submenus?.find(
            sub => sub.id === 'tarefas_atender'
        );
        let requer_atencao = false;

        if (atenderSubmenu) {
            requer_atencao = this.processarBadgeSimples(
                atenderSubmenu,
                contadores
            );
        }

        return {
            submenu: { ...submenu, warn: requer_atencao },
            requer_atencao,
        };
    }

    // ADMINISTRADOR
    private updateBadgesAdministrador(contadores: Contadores) {
        this.links['administrador'] = this.links['administrador'].map(menu => {
            if (
                menu.id === 'requisicoes' ||
                menu.id === 'suprimentos' ||
                menu.id === 'infraestrutura' ||
                menu.id === 'organizacao'
            ) {
                let requer_atencao = false;
                const submenus = menu.submenus.map(submenu => {
                    if (
                        this.processarSubmenuAdministrador(submenu, contadores)
                    ) {
                        requer_atencao = true;
                    }
                    return submenu;
                });

                return {
                    ...menu,
                    warn: requer_atencao,
                    submenus,
                };
            }
            return menu;
        });
    }

    private processarSubmenuAdministrador(
        submenu: SideMenu,
        contadores: Contadores
    ): boolean {
        let requer_atencao = false;
        const statusKey = this.getStatusKey(submenu.id);
        const statusKey2 = submenu.id.split('-').slice(1).join('_');

        if (statusKey in contadores) {
            const valor = contadores[statusKey] || 0;
            requer_atencao = valor > 0;
            submenu.badge = this.criarBadge(valor);
        }

        if (statusKey2 in contadores) {
            const valor = contadores[statusKey2] || 0;
            requer_atencao = valor > 0;
            submenu.warn = requer_atencao;
            submenu.badge = this.criarBadge(valor);

            const indexProcessar = submenu.submenus?.findIndex(
                sub => sub.title === 'Processar'
            );

            if (
                indexProcessar !== undefined &&
                indexProcessar >= 0 &&
                submenu.submenus
            ) {
                submenu.submenus[indexProcessar].badge = this.criarBadge(valor);
            }
        }

        if (!(statusKey in contadores) && !(statusKey2 in contadores)) {
            submenu.badge = undefined;
        }

        return requer_atencao;
    }

    // COORDENADOR
    private updateBadgesCoordenador(contadores: Contadores) {
        const projeto = this.apiProjetoCoordenadorService.getCurrent();
        if (!projeto) return;

        this.links['coordenador'] = this.links['coordenador'].map(menu => {
            if (menu.id === 'requisicoes') {
                return this.processarRequisicoesCoordenador(
                    menu,
                    contadores,
                    projeto.titulo_curto
                );
            }

            if (menu.id === 'suprimentos') {
                return this.processarSuprimentosCoordenador(
                    menu,
                    contadores,
                    projeto.titulo_curto
                );
            }

            return menu;
        });
    }

    private processarSuprimentosCoordenador(
        menu: SideMenu,
        contadores: Contadores,
        tituloCurto: string
    ): SideMenu {
        let warn = false;

        const submenus = menu.submenus.map(submenu => {
            if (submenu.id === 'suprimentos-notas-fiscais') {
                const badges = contadores.notas_projetos?.find(
                    statusProjeto => statusProjeto.titulo_curto === tituloCurto
                );
                const valor = badges?.notas_fiscais || 0;
                submenu.badge = this.criarBadge(valor);
                if (valor > 0) {
                    warn = true;
                }
            }

            if (submenu.id === 'suprimentos-faturas') {
                const badges = contadores.faturas_atestar?.find(
                    ({ titulo_curto }) => titulo_curto === tituloCurto
                );
                const valor = badges?.faturas || 0;
                submenu.badge = this.criarBadge(valor);
                if (valor > 0) {
                    warn = true;
                }
            }

            return submenu;
        });

        return { ...menu, warn, submenus };
    }

    private processarRequisicoesCoordenador(
        menu: SideMenu,
        contadores: Contadores,
        tituloCurto: string
    ): SideMenu {
        let para_atender = false;

        const submenus = menu.submenus.map(submenu => {
            if (submenu.id === 'requisicoes-autorizar') {
                const quantidade =
                    contadores['requisicoes_autorizar']?.[tituloCurto] || 0;
                para_atender = quantidade > 0;
                submenu.badge = this.criarBadge(quantidade);
            } else if (submenu.id in contadores) {
                const valor = contadores[submenu.id] || 0;
                submenu.badge = this.criarBadge(valor);
            } else {
                submenu.badge = undefined;
            }
            return submenu;
        });

        return { ...menu, warn: para_atender, submenus };
    }
}
